import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "custom-progress-container" }
const _hoisted_2 = { class: "text-container" }
const _hoisted_3 = { class: "time-text" }
const _hoisted_4 = { class: "progress-text" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import * as PIXI from 'pixi.js-legacy';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    initialProgress: {},
    step: {},
    standardTargetRatio: {}
  },
  setup(__props: any) {

const CONFIG = {
  // 设计稿比例：162px 容器，10px 线宽，18px 点大小
  BASE_SIZE: 162,
  STROKE_RATIO: 10 / 162,     // 线宽比例
  DOT_RATIO: 18 / 162,        // 进度点大小比例
  LINE_LENGTH: 12,            // 标准指示器线段长度
  LINE_DISTANCE: 6,           // 标准指示器与进度条距离
  ANIMATION_DURATION: 300,     // 动画持续时间
  COLORS:{
    BELOW_TARGET: 0xE70102,         // 未达目标颜色
    ABOVE_TARGET: 0x3CB1FB,         // 已达目标颜色
    BACKGROUND: 0x000000,           // 背景颜色
    DOT_OUTER: 0xFFA314,            // 进度点外圈颜色
    DOT_INNER: 0xFFFFFF,            // 进度点内圈颜色
    INDICATOR: 0xFFFFFF,            // 标准指示器颜色
  },
  OPACITY: {
    BACKGROUND: 0.4,                // 背景圆环透明度
    DOT_GLOW: 0.3,                  // 进度点发光效果透明度
    INDICATOR: 1                  // 标准指示器透明度
  }
}

// 组件属性
const props = __props;

// 容器引用
const containerRef = ref<HTMLDivElement>(null);

// Pixi相关变量
const app = ref<PIXI.Application | null>(null);
const containerSize = ref(0);
const currentProgress = ref(props.initialProgress);
const textProgress = ref(props.initialProgress);

// 图形元素引用
const backgroundCircle = ref<PIXI.GraphicsGeometry | null>(null) as any;
const progressCircle = ref<PIXI.Graphics | null>(null) as any;
const progressDot = ref<PIXI.Graphics | null>(null) as any;
const standardIndicator = ref<PIXI.Graphics | null>(null) as any;

// 计算进度条颜色
const progressColor = computed(() => {
  return currentProgress.value < (props.standardTargetRatio || 0) ? CONFIG.COLORS.BELOW_TARGET : CONFIG.COLORS.ABOVE_TARGET;
});

const strokeWidth = computed(() => {
  return containerSize.value * CONFIG.STROKE_RATIO;
});

// 工具函数：获取圆形几何信息
const getCircleGeometry = () => {
  const size = containerSize.value;
  const stroke = strokeWidth.value;
  const radius = (size / 2) - stroke;
  const centerX = size / 2;
  const centerY = size / 2;
  
  return { size, strokeWidth: stroke, radius, centerX, centerY };
};

// 工具函数：获取角度
const getAngleFromProgress = (progress: number, offset: number = Math.PI / 2) => {
  return (progress / 100) * 2 * Math.PI + offset;
};

// 工具函数：标准化向量
const normalizeVector = (x: number, y: number) => {
  const length = Math.sqrt(x * x + y * y);
  return length > 0 ? { x: x / length, y: y / length } : { x: 0, y: 0 };
};

// 初始化Pixi应用
const initPixiApp = () => {
  if (!containerRef.value) return;  
  // 获取容器尺寸
  const { clientWidth, clientHeight } = containerRef.value;
  const size = Math.min(clientWidth, clientHeight);
  if(size == 0) return;
  containerSize.value = size;

  // 清除现有应用
  if (app.value) {
    app.value.destroy(true);
  }
  
  // 创建Pixi应用，强制使用Canvas渲染器
  app.value = new PIXI.Application({
    width: size,
    height: size,
    backgroundColor: CONFIG.COLORS.BACKGROUND,
    transparent: true,
    antialias: true,
    forceCanvas: true, // 强制使用Canvas渲染器
    resolution: Math.min(window.devicePixelRatio || 1, 2), // 限制分辨率提高性能
    autoDensity: true
  });
  
  // 清空容器并添加Canvas
  containerRef.value.innerHTML = '';
  containerRef.value.appendChild(app.value.view);
  
  initializeGraphics();
  
  // 更新所有图形
  updateGraphics();
};

// 初始化图形元素
const initializeGraphics = () => {
  if (!app.value) return;
  // 创建容器
  const stage = app.value.stage;

  // 创建图形元素
  backgroundCircle.value = new PIXI.Graphics(); // 背景圆环
  progressCircle.value = new PIXI.Graphics(); // 进度圆环
  standardIndicator.value = new PIXI.Graphics();  // 标准指示器
  progressDot.value = new PIXI.Graphics();  // 进度点
  
  stage.addChild(backgroundCircle.value);
  stage.addChild(progressCircle.value);
  stage.addChild(standardIndicator.value);
  stage.addChild(progressDot.value);
}

// 绘制圆形进度条
const drawArc = (graphics: PIXI.Graphics, progress: number, color: number) => {
  console.log('drawArc-----', progress, color);
  const { radius, centerX, centerY } = getCircleGeometry();
  
  graphics.clear();
  graphics.lineStyle(strokeWidth.value, color, 1);
  
  // 计算圆弧角度
  const startAngle = Math.PI / 2; 
  const endAngle = startAngle + (progress / 100) * 2 * Math.PI;
  // 绘制圆弧
  graphics.arc(centerX, centerY, radius, startAngle, endAngle);
};

// 绘制进度点
const drawProgressDot = (graphics: PIXI.Graphics, progress: number) => {
  const { radius, centerX, centerY } = getCircleGeometry();
  const dotSize = containerSize.value * CONFIG.DOT_RATIO;

  // 计算进度点位置
  const angle = getAngleFromProgress(progress);
  const x = centerX + Math.cos(angle) * radius;
  const y = centerY + Math.sin(angle) * radius;

  graphics.clear();
  
  // 尺寸常量
  const OUTER_GLOW_RADIUS = dotSize * 0.7;
  const OUTER_CIRCLE_RADIUS = dotSize * 0.5;
  const INNER_CIRCLE_RADIUS = dotSize * 0.25;
  
  // 外圈发光效果
  graphics.beginFill(CONFIG.COLORS.DOT_OUTER, CONFIG.OPACITY.DOT_GLOW);
  graphics.drawCircle(x, y, OUTER_GLOW_RADIUS);
  graphics.endFill();
  
  // 外圈
  graphics.lineStyle(2, CONFIG.COLORS.DOT_OUTER, 1);
  graphics.drawCircle(x, y, OUTER_CIRCLE_RADIUS);
  
  // 内圈
  graphics.beginFill(CONFIG.COLORS.DOT_INNER);
  graphics.lineStyle(1, CONFIG.COLORS.DOT_OUTER, 1);
  graphics.drawCircle(x, y, INNER_CIRCLE_RADIUS);
  graphics.endFill();
}

// 绘制标准指示器
const drawStandardIndicator = (graphics: PIXI.Graphics, targetRatio: number) => {
  const { radius, centerX, centerY } = getCircleGeometry();
  
  // 计算指示器角度
  const angle = getAngleFromProgress(targetRatio);
  
  // 计算线段位置（在进度条外围）
  const lineDistanceFromCenter = radius + strokeWidth.value / 2 + CONFIG.LINE_DISTANCE;
  const lineX = centerX + Math.cos(angle) * lineDistanceFromCenter;
  const lineY = centerY + Math.sin(angle) * lineDistanceFromCenter;
  
  // 计算指向圆心的方向向量
  const dirX = centerX - lineX;
  const dirY = centerY - lineY;
  const normalizedDir = normalizeVector(dirX, dirY);
  
  // 线段长度（按比例缩放）
  const lineLength = CONFIG.LINE_LENGTH * (containerSize.value / CONFIG.BASE_SIZE);
  
  // 计算线段端点
  const startX = lineX + normalizedDir.x * (lineLength / 2);
  const startY = lineY + normalizedDir.y * (lineLength / 2);
  const endX = lineX - normalizedDir.x * (lineLength / 2);
  const endY = lineY - normalizedDir.y * (lineLength / 2);
  
  graphics.clear();
  graphics.lineStyle(1, CONFIG.COLORS.INDICATOR, CONFIG.OPACITY.INDICATOR);
  graphics.moveTo(startX, startY);
  graphics.lineTo(endX, endY);
};

// 绘制背景圆环
const drawBackgroundCircle = (graphics: PIXI.Graphics) => {
  const { radius, centerX, centerY } = getCircleGeometry();
  
  graphics.clear();
  graphics.lineStyle(strokeWidth.value, CONFIG.COLORS.BACKGROUND, CONFIG.OPACITY.BACKGROUND);
  graphics.drawCircle(centerX, centerY, radius);
};

// 更新所有图形
const updateGraphics = () => {
  if (!containerSize.value || !app.value) return;
  
  // 只有尺寸变化时才重绘背景
  if (backgroundCircle.value) {
    drawBackgroundCircle(backgroundCircle.value);
  }
  // 进度变化时重绘进度条
  if (progressCircle.value) {
    drawArc(progressCircle.value, currentProgress.value, progressColor.value);
  }
  
  // 标准指示器变化时重绘
  if (standardIndicator.value && props.standardTargetRatio !== undefined) {
    drawStandardIndicator(standardIndicator.value, props.standardTargetRatio);
  }
  
  // 进度点总是需要更新（位置变化）
  if (progressDot.value) {
    drawProgressDot(progressDot.value, currentProgress.value);
  }
};

// 资源清理函数
const cleanupResources = () => {
  
  // 清理图形元素
  const graphicsElements = [
    backgroundCircle,
    progressCircle,
    progressDot,
    standardIndicator
  ];
  
  graphicsElements.forEach(element => {
    if (element.value) {
      element.value.destroy();
      element.value = null;
    }
  });
  
  // 清理Pixi应用
  if (app.value) {
    app.value.destroy(true);
    app.value = null;
  }
};

// 更新容器尺寸
const updateContainerSize = () => {
  if (!containerRef.value) return;
  
  const { clientWidth, clientHeight } = containerRef.value;
  const newSize = Math.min(clientWidth, clientHeight);
  
  if (newSize !== containerSize.value) {
    containerSize.value = newSize;
    
    if (app.value) {
      app.value.renderer.resize(newSize, newSize);
      updateGraphics();
    } else {
      initPixiApp();
    }
  }
};

// 监听进度变化
watch(() => props.initialProgress, (newProgress, oldProgress) => {
  textProgress.value = newProgress
  const from = oldProgress !== -1 ? oldProgress : 0;
  const to = newProgress !== -1 ? newProgress : 0;
  
  currentProgress.value = to;
  updateGraphics();
}, { immediate: true });

// 监听标准目标比例变化
watch(() => props.standardTargetRatio, () => {
  updateGraphics();
});


// 生命周期
onMounted(() => {
  nextTick(() => {
    initPixiApp();
    window.addEventListener('resize', updateContainerSize);
    
    // 监听容器尺寸变化
    if (containerRef.value && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(updateContainerSize);
      resizeObserver.observe(containerRef.value);
      
      onUnmounted(() => {
        resizeObserver.disconnect();
      });
    }
  });
});

onUnmounted(() => {
  cleanupResources();
  window.removeEventListener('resize', updateContainerSize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "progress-wrapper",
      ref_key: "containerRef",
      ref: containerRef
    }, null, 512),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("p", _hoisted_3, "未覆盖", 512), [
        [_vShow, textProgress.value == -1]
      ]),
      _withDirectives(_createElementVNode("p", _hoisted_4, [
        _createElementVNode("span", null, _toDisplayString(textProgress.value), 1),
        _cache[0] || (_cache[0] = _createTextVNode()),
        _cache[1] || (_cache[1] = _createElementVNode("span", null, "%", -1))
      ], 512), [
        [_vShow, textProgress.value != -1]
      ]),
      _cache[2] || (_cache[2] = _createElementVNode("p", { class: "progress-title-text" }, "目标画面占比", -1))
    ])
  ]))
}
}

})